import * as React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { Seat, GdsSeatService, ServicesMap } from '../../../types';
import { useTheme } from '../../../../theme';
import Amenities from '../../../../Amenities';

import { Money as MoneyType } from '../../../../types';
import { useConfig } from '../../../../context';
import { useSeatAdditionalInfo } from '../hooks';
import SeatAdditionalInfo from '../SeatAdditionalInfo/SeatAdditionalInfo';
import SeatPrice from '../../SeatPrice/SeatPrice';
import { CdnIcon, SafeHtml } from '@shared/ui';
import { FlightStaticInfoFragment, PreselectedSeatFragment, SeatComfort } from '@websky/graphql';
import { Tooltip } from '../../../../BaseComponents';
import { isExistI18nTranslate } from '../../../../utils';

interface Props {
	className?: string;
	seatService: GdsSeatService;
	hideImage?: boolean;
	fullInfo?: boolean;
	amenities?: FlightStaticInfoFragment['amenities'];
	seats?: Seat[];
	segmentId?: string;
	preselectedSeat?: PreselectedSeatFragment['seat'];
	isFreeSeat?: boolean;
	seatPrice?: MoneyType;
	services?: ServicesMap;
	passengerId?: string;
	dataColorNum?: string;
	renderHeader?: () => React.ReactNode;
}

const SeatInfo: React.FC<Props> = ({
	className,
	seatService,
	hideImage,
	fullInfo,
	amenities,
	seats,
	segmentId,
	preselectedSeat,
	isFreeSeat,
	seatPrice,
	services,
	passengerId,
	dataColorNum,
	renderHeader
}) => {
	const theme = useTheme('SeatMap').SeatsInfoStyles;

	const { t } = useTranslation('SeatMap');

	const iataCode = useConfig().global.companyInfo.iataCode;
	const showSeatPriceNotServicePrice = ['DV', 'A9', 'RM'].includes(iataCode);

	const mainSeat = seats?.[0];

	const { restrictions, permissions, additional, withInfo } = useSeatAdditionalInfo(seats);

	const servicePrice = React.useMemo(() => {
		const actualService = services?.[segmentId]?.[passengerId]?.[seatService.rfisc];

		if (actualService && !showSeatPriceNotServicePrice) {
			return actualService.price;
		}

		return seatService?.price;
	}, [showSeatPriceNotServicePrice, services, segmentId, passengerId]);

	const renderFeatures = () => {
		if (seatService?.parameters?.length) {
			return (
				<div className={theme.features}>
					{seatService.parameters.map((param, index) => {
						return (
							<div className={theme.feature} key={index}>
								{param.title}: {param.value}
							</div>
						);
					})}
				</div>
			);
		}

		return null;
	};

	const renderTooltipInfo = useCallback(() => {
		if (!isExistI18nTranslate(`SeatInfo_TooltipText_${seatService.rfisc}`, { ns: 'SeatMap' })) {
			return null;
		}

		return (
			<Tooltip
				classes={{ popper: theme.tooltip__popper }}
				title={t(`SeatInfo_TooltipText_${seatService.rfisc}`)}
				enterTouchDelay={0}
				leaveTouchDelay={1500}
			>
				<i className={theme.description__info}>
					<CdnIcon icon="Info/Details" />
				</i>
			</Tooltip>
		);
	}, [seatService.rfisc]);

	let seatImage: string;
	if (!isFreeSeat && seatService?.infoBySegments) {
		seatImage = seatService.infoBySegments.find(item => item.segment.id === segmentId)?.image;
	}

	const seatDescription = fullInfo && amenities?.length > 0 ? renderFeatures() : seatService?.description?.trim();
	const showAmenities = fullInfo && amenities?.length > 0;
	const onlyPrice = !showAmenities && !renderFeatures();

	return (
		<div
			className={cn(
				theme.seat,
				{
					[theme.seat_economy]: seatService?.comfort === SeatComfort.Standart,
					[theme.seat_preferred]: seatService?.comfort === SeatComfort.Preferred,
					[theme.seat_business]: seatService?.comfort === SeatComfort.Comfort,
					[theme.seat_extended]: mainSeat?.isMoreSpaceForLegs,
					[theme.seat_free]: isFreeSeat
				},
				className
			)}
			key={seatService?.id || mainSeat?.number + segmentId}
			data-rfisc={seatService?.rfisc}
			data-color-num={dataColorNum}
		>
			{!isFreeSeat && (seatImage || seatService?.serviceImage) && !hideImage && (
				<img className={theme.image} src={seatImage || seatService?.serviceImage} alt={seatService?.name} />
			)}

			{renderHeader?.() ?? (
				<div className={theme.header}>
					<div
						className={cn(theme.title, {
							[theme.title_withDescription]: seatDescription
						})}
						data-rfisc={seatService?.rfisc}
					>
						{preselectedSeat?.number && <span>{preselectedSeat.number}</span>}

						{isFreeSeat ? (
							mainSeat?.number
						) : (
							<>
								{seatService?.name}

								{typeof seatDescription === 'string' ? (
									<div className={theme.description__wrapper}>
										<SafeHtml className={theme.description} html={seatDescription} />
										{renderTooltipInfo()}
									</div>
								) : (
									<div className={theme.description}>{seatDescription}</div>
								)}
							</>
						)}
					</div>
				</div>
			)}

			<div className={theme.lineSeparator__wrapper}>
				<div className={theme.lineSeparator} />
			</div>

			<div className={cn(theme.seat__info, { [theme.seat__info_onlyPrice]: onlyPrice })}>
				{showAmenities ? <Amenities amenities={amenities} /> : renderFeatures()}

				<div className={theme.price__wrapper}>
					{isFreeSeat ? (
						t('Free seat')
					) : (
						<SeatPrice
							className={theme.seatPrice}
							moneyClassName={theme.price}
							money={seatPrice ?? servicePrice}
							priceFrom={!seatPrice}
						/>
					)}
				</div>
			</div>

			{mainSeat && withInfo && (
				<div className={theme.note}>
					<div className={theme.note__header}>
						<CdnIcon icon="Info/Warning" /> {t('Note')}
					</div>
					<SeatAdditionalInfo restrictions={restrictions} permissions={permissions} additional={additional} />
				</div>
			)}
		</div>
	);
};

export default SeatInfo;
